import { Component } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { AdoptionTrackingService } from './shared/services/adoption-tracking.service';
import { AppUpdateService } from './shared/services/app-update.service';
import { AuthService } from './shared/services/auth.service';
import { SiblingService } from './shared/services/sibling.service';
import { Router } from '@angular/router';
import { Platform } from '@ionic/angular';
import { FcmService } from './services/fcm/fcm.service';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
// import { AppServiceService } from './service/app-service.service';
import { StatusBar, Style } from '@capacitor/status-bar';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent {
  user: any;
  public appPages;
  public sessions;
  public session;
  public labels = ['Family', 'Friends', 'Notes', 'Work', 'Travel', 'Reminders'];
  public safeUrl: SafeUrl;
  userInitials: string = '';

  constructor(
    private authService: AuthService,
    private adoptionTracking: AdoptionTrackingService,
    private appUpdate: AppUpdateService,
    private siblingService: SiblingService,
    private router: Router,
    private platform: Platform,
    private fcm : FcmService,
    private sanitizer: DomSanitizer
  ) {
    this.initializeApp();
  }

  async initializeApp() {
    if (Capacitor.getPlatform() === 'web') {
      return;
    }

    try {
      await Keyboard.setResizeMode({ mode: KeyboardResize.Ionic });
      Keyboard.addListener('keyboardWillShow', (info) => {
        document.documentElement.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`);
      });
      Keyboard.addListener('keyboardDidShow', (info) => {
        document.documentElement.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`);
      });
      Keyboard.addListener('keyboardWillHide', () => {
        document.documentElement.style.setProperty('--keyboard-height', '0px');
      });
      Keyboard.addListener('keyboardDidHide', () => {
        document.documentElement.style.setProperty('--keyboard-height', '0px');
      });
    } catch (error) {
      console.error('Keyboard setup failed', error);
    }
  }
  
  ngOnInit() {
    this.adoptionTracking.start();
    this.user = this.authService.getLoggedInUser();
    let profileImage = localStorage.getItem('profile_image');
    if(profileImage != "null" && profileImage != null){
      this.safeUrl = this.sanitizer.bypassSecurityTrustUrl(profileImage);
    }

    // Generate user initials for avatar placeholder
    if (this.user) {
      const fullName = this.user.full_name || '';
      const nameParts = fullName.trim().split(' ');
      if (nameParts.length >= 2) {
        this.userInitials = (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
      } else if (nameParts.length === 1) {
        this.userInitials = nameParts[0].charAt(0).toUpperCase();
      } else {
        this.userInitials = 'U';
      }

      if (this.user.is_student) {
        this.siblingService.loadSiblings().subscribe();
      }
    }

    this.appPages = [
      { title: 'Home Page', url: '/home', icon: 'home', visible: this.user?.is_student },
      { title: 'Home', url: '/staff-home', icon: 'home', visible: this.user?.is_teacher },
      { title: 'Face Attendance', url: '/staff-attendance-report', icon: 'finger-print', visible: !this.user.is_student },
      { 
        title: 'QR Attendance', 
        url: '/qr-attendance', 
        icon: 'qr-code', 
        visible: this.authService.hasPermission({ permissions: ['qr-exam-attendance.module'], allowed_roles: [] }),
      },
      {
        title: 'Attendance',
        url: '/attendance',
        icon: 'today',
        visible: this.user?.is_student,
      },
      { title: 'Fees', url: '/fees', icon: 'cash', visible: this.user?.is_student },
      { title: 'Ask about child', url: '/parent-chat', icon: 'chatbubbles', visible: this.user?.is_student },
      {
        title: 'Assignment',
        url: this.user.is_teacher ? '/assignment/list' : 'student-assignments/list',
        icon: 'document-text',
        visible: this.user?.is_teacher || this.user?.is_student,
      },
      {
        title: 'Class/Home Work',
        url: this.user?.is_teacher ? '/class-work/list' : 'student-class-work/list',
        icon: 'book',
        visible: this.user?.is_teacher || this.user?.is_student,
      },
      {
        title: 'Notices',
        url: '/notices/list',
        icon: 'notifications',
        visible: this.authService.hasPermission({ permissions: ['notices.module'], allowed_roles: [] }),
      },
      {
        title: 'Time Table',
        url: '/timetable',
        icon: 'time',
        visible: this.user?.is_teacher || this.user?.is_student,
      },
      {
        title: 'Students',
        url: '/form-teacher-students',
        icon: 'people',
        visible: !!this.user?.is_teacher && !!this.user?.is_form_teacher,
      },
      { title: 'Result', url: '/result', icon: 'reader', visible: this.user?.is_student },
      { title: 'Admit Card', url: '/admit-card', icon: 'id-card', visible: this.user?.is_student },
      { title: 'Report Card', url: '/report-card', icon: 'document-text', visible: this.user?.is_student },
      {
        title: 'Change Password',
        url: '/change-password',
        icon: 'key',
        visible: true,
      },
      {
        title: 'Expenses',
        url: '/expenses',
        icon: 'receipt',
        visible:
          this.authService.hasPermission({
            permissions: ['expense.module'],
            allowed_roles: [],
          }),
      },
      { 
        title: 'House Events', 
        url: '/house-management', 
        icon: 'calendar', 
        visible: this.authService.hasPermission({ permissions: ['house.management.module'], allowed_roles: [] }),
        permissions: [],
        allowed_roles: [],
      },
      { 
        title: 'Daily Events', 
        url: '/house-management/daily-events', 
        icon: 'repeat', 
        visible: this.authService.hasPermission({ permissions: ['house.management.module'], allowed_roles: [] }),
        permissions: [],
        allowed_roles: [],
      },
      { 
        title: 'Department Messages', 
        url: '/department-messages', 
        icon: 'mail', 
        visible: this.authService.hasPermission({ permissions: ['department.messages.module'], allowed_roles: [] }),
        permissions: [],
        allowed_roles: [],
      }
    ];
    let code = localStorage.getItem('code');

    if (code) {
      this.router.navigateByUrl('/login');
    }
    
    this.platform.ready().then(() => {
        this.appUpdate.check();
        this.fcm.initPush();
      }).catch((error) => {
        console.error('Error initializing FCM:', error);
      }
    );

    this.platform.resume.subscribe(() => {
      this.appUpdate.check();
      const path = this.router.url.split('?')[0];
      if (
        path === '/code' ||
        path.startsWith('/code/') ||
        path === '/login' ||
        path.startsWith('/login/') ||
        path === '/login2' ||
        path.startsWith('/login2/')
      ) {
        return;
      }
      if (!this.authService.isUserLoggedIn()) {
        this.authService.logout();
      }
    });
    
   // this.loadSessions();
  }

  logout() {
    this.siblingService.clear();
    this.authService.logout();
  }

  checkIfTeacher() {
    if (this.user.user_type == 'staff') {
      return false;
    } else {
      return true;
    }
  }
  checkIfStudent() {
    if (this.user.user_type == 'student') {
      return false;
    } else {
      return true;
    }
  }

  changeSession(e) {
    localStorage.setItem('session', e.detail.value);
    
    
    window.location.reload();
  }

  // loadSessions() {
  //   this.appService.getSessions().subscribe(result => {
  //     this.sessions = result;
  //     let selectedSessionId =  localStorage.getItem('session');
  //     this.sessions.forEach((element) => {
  //       if (element.id == selectedSessionId) {
  //         this.session = element;
  //       }
  //     });
  //   });...................................................................
  // }

  pageVisible(page) {
    if (page == 'Home Page') {
      return this.checkIfTeacher();
    } else if (page == 'Home') {
      return !this.checkIfTeacher();
    }else if (page == 'Face Attendance') {
      return !this.checkIfTeacher();
    } else if (page == 'Fees') {
      return this.checkIfTeacher();
    } else if (page == 'Assignment') {
      return this.checkIfTeacher();
    } else if (page == 'Result') {
      return this.checkIfTeacher();
    } else {
      return true;
    }
  }

  viewProfile() {
    if(this.user.is_student){
      this.router.navigateByUrl('/profile');
    } else {
      this.router.navigateByUrl('/profile');
    }
  }
}
